import {Injectable} from "@angular/core";
import {HttpClient, HttpParams, HttpResponse} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {ExamSession, ShortExamSession, ExamSessionCreateOrUpdateData} from "../../models/examSession";
import {SessionFilterEnum} from "../../enums/sessionFilterEnum";


@Injectable({
    providedIn: "root"
})
export class ExamSessionService {

    constructor (private http: HttpClient) { }

    list (page: number = null, term?: string, filters?: Array<SessionFilterEnum>,): Observable<Array<ExamSession>> {
        let params = new HttpParams();

        if (page) {
            params = params.set(
                "page",
                `${page}`
            );
        }

        if (term) {
            params = params.set(
                "term",
                `${term}`
            );
        }

        if (filters) {
            params = params.set(
                "filters",
                filters.join(",")
            );
        }

        return this.http.get<Array<ExamSession>>(
            `${environment.baseApiUrl}/exam_sessions/`,
            {params}
        ).
            pipe(map((res) => res.map((d) => new ExamSession(d))));
    }

    listPlannedOnly (): Observable<Array<ExamSession>> {
        let params = new HttpParams();

        params = params.set(
            "filters",
            "planned"
        );

        return this.http.get<Array<ExamSession>>(
            `${environment.baseApiUrl}/exam_sessions/`,
            {params}
        ).
            pipe(map((res) => res.map((d) => new ExamSession(d))));
    }

    shortList (centerId = null, upcomingOnly = false): Observable<Array<ShortExamSession>> {
        let params = new HttpParams();

        params = params.set(
            "short",
            "True"
        );

        if (upcomingOnly) {
            params = params.set(
                "filters",
                "planned"
            );
        }

        if (centerId) {
            params = params.set(
                "center_id",
                `${centerId}`
            );
        }

        return this.http.get<Array<ShortExamSession>>(
            `${environment.baseApiUrl}/exam_sessions/`,
            {params}
        ).
            pipe(map((res) => res.map((d) => new ShortExamSession(d))));
    }

    countByStatus (term?: string): Observable<{total: number, terminated: number, planned: number}> {
        let params = new HttpParams();

        if (term) {
            params = params.set("term", `${term}`);
        }

        return this.http.get<{total: number, terminated: number, planned: number}>(
            `${environment.baseApiUrl}/exam_sessions/count_by_status/`,
            {params}
        );
    }

    count (term: string, filters?: Array<SessionFilterEnum>): Observable<number> {
        let params = new HttpParams();

        if (term) {
            params = params.set(
                "term",
                `${term}`
            );
        }

        if (filters) {
            params = params.set(
                "filters",
                filters.join(",")
            );
        }

        return this.http.get<number>(
            `${environment.baseApiUrl}/exam_sessions/count/`,
            {params}
        );
    }
    update (id: number, data: ExamSessionCreateOrUpdateData) {
        return this.http.patch<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/`,
            data
        ).pipe(map((res) => new ExamSession(res)));
    }

    create (data: ExamSessionCreateOrUpdateData) {
        return this.http.post<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/`,
            data
        ).pipe(map((res) => new ExamSession(res)));
    }

    destroy (id: number) {
        return this.http.delete<ExamSession>(`${environment.baseApiUrl}/exam_sessions/${id}/`);
    }

    reportIncident (id: number, comment?: string) {
        return this.http.post<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/report_incident/`,
            comment ? {comment} : {}
        ).pipe(map((res) => new ExamSession(res)));
    }

    confirmSupervisorAttendance (id: number) {
        return this.http.put<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/confirm_supervisor_attendance/`,
            {}
        ).pipe(map((res) => new ExamSession(res)));
    }

    confirmCorrectorAttendance (id: number) {
        return this.http.put<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/confirm_corrector_attendance/`,
            {}
        ).pipe(map((res) => new ExamSession(res)));
    }

    exportToXml (id: number, examIds) {
        return this.http.get<string>(
            `${environment.baseApiUrl}/exam_sessions/${id}/export_to_xml/?${examIds.map((id) => `exam_ids=${id}`).join("&")}`,
            {}
        );
    }

    open (id: number) {
        return this.http.post<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/open/`,
            {}
        ).pipe(map((res) => new ExamSession(res)));
    }

    close (id: number) {
        return this.http.post<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/close/`,
            {}
        ).pipe(map((res) => new ExamSession(res)));
    }

    comment (id: number, comment: string) {
        return this.http.post<ExamSession>(
            `${environment.baseApiUrl}/exam_sessions/${id}/comment/`,
            {comment}
        ).pipe(map((res) => new ExamSession(res)));
    }

    minutePdf (id: number): Observable<HttpResponse<Blob>> {
        return this.http.get(
            `${environment.baseApiUrl}/exam_sessions/${id}/minute_pdf/`,
            {
                responseType: "blob",
                observe: "response" // Observe the full response
            }
        );
    }
}
